import { Plus, Minus, ShoppingCart, PackageOpen, Lock, ArrowRight } from "lucide-react";
import { getAnalytics, logEvent } from "firebase/analytics";
import { httpsCallable } from "firebase/functions";
import { app, functions } from "../../firebase-config";
import styles from "./Checkout.module.css";
import { useState, useEffect } from "react";

const Checkout = ({ cartItems, setCartItems }) => {
const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Firebase Analytics
  const analytics = getAnalytics(app);

  // Create callable function reference
  const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

  // Track page view
  useEffect(() => {
    const totalValue = Object.values(cartItems).reduce(
      (sum, item) => sum + (item.price * item.quantity),
      0
    );

    logEvent(analytics, "page_view", {
      page_title: "Checkout",
      page_location: window.location.href,
      page_path: "/checkout"
    });

    // Also log view_cart event
    if (Object.keys(cartItems).length > 0) {
      logEvent(analytics, "view_cart", {
        currency: "USD",
        value: totalValue / 100,
        items: Object.entries(cartItems).map(([id, item]) => ({
          item_id: id,
          item_name: `Qwytt ${id} Pack`,
          price: item.price / 100,
          quantity: item.quantity
        }))
      });
    }
  }, [analytics, cartItems]);

async function onReserve() {
    setIsLoading(true);
    setError(null);

    const totalValue = Object.values(cartItems).reduce(
      (sum, item) => sum + (item.price * item.quantity),
      0
    );

    // Log checkout attempt
    logEvent(analytics, "checkout_progress", {
      checkout_step: 1,
      checkout_option: "stripe",
      currency: "USD",
      value: totalValue / 100
    });

    try {
      const requestData = {
        cartItems,
        uid: null,
        email: null,
        isPreorder: true,
        estimatedShipDate: 'Q4 2025'
      };

      console.log('🚀 Calling createCheckoutSession:', requestData);

      // Call the Cloud Function using Firebase SDK
      const result = await createCheckoutSession(requestData);
      
      console.log('✅ Function result:', result.data);

      if (result.data.error) {
        throw new Error(result.data.error);
      }

      if (!result.data.url) {
        throw new Error('No checkout URL received from server');
      }

      console.log('🔗 Redirecting to Stripe:', result.data.url);
      
      // Log successful redirect to Stripe
      logEvent(analytics, "checkout_redirect", {
        currency: "USD",
        value: totalValue / 100,
        payment_type: "stripe"
      });
      
      // Redirect to Stripe
      window.location.href = result.data.url;
    } catch (e) {
      console.error("❌ Checkout error:", e);
      setError(e.message || 'Failed to start checkout');
      
      // Log checkout error
      logEvent(analytics, "checkout_error", {
        error_message: e.message || 'Failed to start checkout',
        currency: "USD",
        value: totalValue / 100
      });
      
      alert("Error: " + (e.message || "Failed to start checkout"));
    } finally {
      setIsLoading(false);
    }
  }

  function formatPrice(amount) {
    return (amount / 100).toFixed(2);
  }

  function totalPrice() {
    let total = 0;
    Object.values(cartItems).forEach((item) => {
      total += item.price * item.quantity;
    });
    return total;
  }

  function addItem(id, price) {
    // Log add to cart from checkout
    logEvent(analytics, "add_to_cart", {
      currency: "USD",
      value: price / 100,
      items: [{
        item_id: id,
        item_name: `Qwytt ${id} Pack`,
        price: price / 100,
        quantity: 1
      }],
      location: "checkout_page"
    });

    setCartItems((prev) => ({
      ...prev,
      [id]: {
        quantity: (prev[id]?.quantity ?? 0) + 1,
        price,
      },
    }));
  }

  function removeItem(id) {
    const item = cartItems[id];
    if (item) {
      // Log remove from cart
      logEvent(analytics, "remove_from_cart", {
        currency: "USD",
        value: item.price / 100,
        items: [{
          item_id: id,
          item_name: `Qwytt ${id} Pack`,
          price: item.price / 100,
          quantity: 1
        }],
        location: "checkout_page"
      });
    }

    setCartItems((prev) => {
      if (!prev[id]) return prev;
      const updated = { ...prev };
      const qty = updated[id].quantity;

      if (qty <= 1) delete updated[id];
      else updated[id].quantity = qty - 1;

      return updated;
    });
  }

  const subtotal = totalPrice();
  const shipping = 599; // $5.99 in cents
  const total = subtotal + shipping;
  const itemCount = Object.values(cartItems).reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Left Column - Cart Items */}
        <div className={styles.leftColumn}>
          <div className={styles.headerSection}>
            <h1 className={styles.title}>Your Cart</h1>
            {itemCount > 0 && (
              <span className={styles.itemCount}>{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
            )}
          </div>

          {Object.keys(cartItems).length === 0 ? (
            <div className={styles.emptyState}>
              <PackageOpen size={64} className={styles.emptyIcon} />
              <h2>Your cart is empty</h2>
              <p>Add some Qwytt devices to get started</p>
            </div>
          ) : (
            <div className={styles.cartItems}>
              {Object.entries(cartItems).map(([id, item]) => (
                <div key={id} className={styles.cartItem}>
                  <div className={styles.itemImage}>
                    <div className={styles.imagePlaceholder}>
                      <PackageOpen size={32} />
                    </div>
                  </div>

                  <div className={styles.itemDetails}>
                    <h3 className={styles.itemName}>
                      Qwytt {id.charAt(0).toUpperCase() + id.slice(1)} Pack
                    </h3>
                    <p className={styles.itemDescription}>
                      Early Access Device - Limited Edition
                    </p>
                    <div className={styles.itemMeta}>
                      <span className={styles.itemPrice}>
                        ${formatPrice(item.price)}
                      </span>
                      <span className={styles.itemQtyLabel}>
                        Qty: {item.quantity}
                      </span>
                    </div>
                  </div>

                  <div className={styles.itemActions}>
                    <div className={styles.qtyControl}>
                      <button
                        className={styles.qtyBtn}
                        onClick={() => removeItem(id)}
                        aria-label="Decrease quantity"
                      >
                        <Minus size={16} />
                      </button>
                      <span className={styles.qtyDisplay}>{item.quantity}</span>
                      <button
                        className={styles.qtyBtn}
                        onClick={() => addItem(id, item.price)}
                        aria-label="Increase quantity"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    <div className={styles.itemTotal}>
                      ${formatPrice(item.price * item.quantity)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Column - Order Summary */}
        {Object.keys(cartItems).length > 0 && (
          <div className={styles.rightColumn}>
            <div className={styles.summaryCard}>
              <h2 className={styles.summaryTitle}>Order Summary</h2>

              <div className={styles.summaryContent}>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Subtotal</span>
                  <span className={styles.summaryValue}>${formatPrice(subtotal)}</span>
                </div>

                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>
                    Shipping
                    <span className={styles.shippingNote}>Standard (5-8 days)</span>
                  </span>
                  <span className={styles.summaryValue}>${formatPrice(shipping)}</span>
                </div>

                <div className={styles.divider}></div>

                <div className={styles.summaryRow + ' ' + styles.summaryTotal}>
                  <span className={styles.summaryLabel}>Total</span>
                  <span className={styles.summaryValue}>${formatPrice(total)}</span>
                </div>
              </div>

              {error && (
                <div className={styles.errorMessage}>
                  <span>⚠️ {error}</span>
                </div>
              )}

              <button 
                className={styles.checkoutBtn} 
                onClick={onReserve}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span>Processing...</span>
                ) : (
                  <>
                    <span>Proceed to Checkout</span>
                    <ArrowRight size={20} />
                  </>
                )}
              </button>

              <div className={styles.secureNote}>
                <Lock size={14} />
                <span>Secure checkout powered by Stripe</span>
              </div>

              <div className={styles.benefits}>
                <div className={styles.benefit}>
                  <div className={styles.benefitIcon}>✓</div>
                  <span>Fully refundable before shipping</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;